import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, CheckCircle, XCircle, Loader2, Star, ChevronDown, ChevronUp, Trash2, AlertTriangle } from "lucide-react";

// IST date formatter: "26-Aug-26"
const formatIST = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const day = String(ist.getUTCDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mon = months[ist.getUTCMonth()];
  const yr = String(ist.getUTCFullYear()).slice(-2);
  return `${day}-${mon}-${yr}`;
};

const formatISTTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const h = String(ist.getUTCHours()).padStart(2, '0');
  const m = String(ist.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
};

export default function NegotiationTable({ request, viewerRole, onUpdate, isPreview, products, marketTrends, effectiveStock, autoRates }) {
  const [sending, setSending] = useState(false);
  const [counterRates, setCounterRates] = useState({});
  const [showCounterRow, setShowCounterRow] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [counterNotes, setCounterNotes] = useState("");
  const [newGrades, setNewGrades] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [gradeCancelDialog, setGradeCancelDialog] = useState(null); // { key, grade, sku }
  const [gradeCancelRemark, setGradeCancelRemark] = useState("");

  if (!request) return null;

  // Helper: get effective stock for a grade+sku
  const getStock = (grade, sku) => {
    if (effectiveStock && Array.isArray(effectiveStock)) {
      const es = effectiveStock.find(s => s.grade === grade && s.sku === sku);
      if (es) return es.effective ?? 0;
    }
    if (!products || !Array.isArray(products)) return null;
    const p = products.find(pr => pr.grade === grade && (pr.sku_type || pr.sku) === sku);
    return p ? (p.qty ?? p.quantity ?? 0) : null;
  };

  // Helper: get auto-suggested rate for customer
  const getAutoRate = (grade, sku) => {
    if (!autoRates) return null;
    return autoRates[`${grade}|${sku}`] || null;
  };

  const negotiations = request.negotiations || [];
  const isActive = ['pending', 'negotiating', 'countered'].includes(request.status);
  const lastRound = negotiations[negotiations.length - 1];
  const waitingForOwner = lastRound?.from === 'customer';
  const waitingForCustomer = lastRound?.from === 'owner';
  const myTurn = (viewerRole === 'owner' && waitingForOwner) || (viewerRole === 'customer' && waitingForCustomer);

  // Build grade map from ALL rounds (including deleted grades)
  const gradeMap = {};
  const allGradeKeys = [];
  const gradeKeySet = new Set();

  // Collect grades from ALL negotiation rounds so deleted grades still appear
  negotiations.forEach(neg => {
    if (neg.action) return;
    (neg.items || []).forEach(item => {
      const key = `${item.grade}|${item.sku || 'TIN'}`;
      if (!gradeKeySet.has(key)) {
        gradeKeySet.add(key);
        gradeMap[key] = { grade: item.grade, sku: item.sku || 'TIN', qty: item.qty, rate: item.rate || item.customer_rate || 0, rounds: [] };
        allGradeKeys.push(key);
      }
    });
  });

  // Also include latest_items
  (request.latest_items || []).forEach(item => {
    const key = `${item.grade}|${item.sku || 'TIN'}`;
    if (!gradeKeySet.has(key)) {
      gradeKeySet.add(key);
      gradeMap[key] = { grade: item.grade, sku: item.sku || 'TIN', qty: item.qty, rate: item.rate || item.customer_rate || 0, rounds: [] };
      allGradeKeys.push(key);
    }
    gradeMap[key].qty = item.qty;
  });

  negotiations.forEach((neg) => {
    if (neg.action) return;
    allGradeKeys.forEach(key => {
      const gd = gradeMap[key];
      const matchItem = (neg.items || []).find(i => `${i.grade}|${i.sku || 'TIN'}` === key);
      gd.rounds.push({
        round: neg.round,
        from: neg.from,
        rate: matchItem ? (matchItem.rate || matchItem.owner_rate || matchItem.customer_rate || 0) : null,
        qty: matchItem ? matchItem.qty : null,
        present: !!matchItem
      });
    });
  });

  const roundHeaders = [];
  negotiations.forEach(neg => {
    if (neg.action) return;
    roundHeaders.push({ round: neg.round, from: neg.from, label: neg.from === 'customer' ? `Cust R${neg.round}` : `Owner R${neg.round}`, notes: neg.notes, timestamp: neg.timestamp });
  });

  // If deal is confirmed/ordered/processing, mark the last round header as "Confirm"
  const isFinalized = ['processing', 'confirmed', 'ordered', 'order_cancelled'].includes(request.status);
  if (isFinalized && roundHeaders.length > 0) {
    const last = roundHeaders[roundHeaders.length - 1];
    last.label = 'Confirm';
  }

  // Find who confirmed/rejected/cancelled
  const actionNeg = negotiations.find(n => n.action === 'confirmed' || n.action === 'rejected' || n.action === 'order_cancelled');
  const actionBy = actionNeg?.from || actionNeg?.confirmed_by || '';

  const hasHistory = roundHeaders.length > 2;
  const historyHeaders = hasHistory && !showHistory ? [] : roundHeaders.slice(0, -2);
  const latestHeaders = roundHeaders.slice(Math.max(0, roundHeaders.length - 2));

  // Auto-fill rates from market trends into counter
  const autoFillRates = (rateType) => {
    if (!marketTrends || !Array.isArray(marketTrends) || marketTrends.length === 0) {
      toast.error("Market trends data not available");
      return;
    }
    const newRates = { ...counterRates };
    let filled = 0;
    allGradeKeys.forEach(key => {
      const gd = gradeMap[key];
      // Try exact match first, then match by grade only
      let trend = marketTrends.find(t => t.grade === gd.grade && t.sku === gd.sku);
      if (!trend) trend = marketTrends.find(t => t.grade === gd.grade);
      if (!trend) return;
      const cr = trend.customer_rate_na ? 0 : (trend.customer_rate || 0);
      if (cr <= 0) return;
      let rate = cr;
      if (rateType === 'bsr1') rate = cr + 100;
      else if (rateType === 'bsr2') rate = cr + 200;
      else if (rateType === 'bsr3') rate = cr + 300;
      newRates[key] = String(rate);
      filled++;
    });
    setCounterRates(newRates);
    if (!showCounterRow) setShowCounterRow(true);
    if (filled > 0) toast.success(`${filled} rates auto-filled: ${rateType === 'cr' ? 'Customer Rate' : rateType.toUpperCase()}`);
    else toast.info("No matching grades found in market trends");
  };

  const sendCounter = async () => {
    setSending(true);
    const endpoint = viewerRole === 'owner' 
      ? `${API}/order-requests/${request.id}/counter`
      : `${API}/order-requests/${request.id}/customer-counter`;
    
    const items = allGradeKeys.map(key => {
      const gd = gradeMap[key];
      const lastPresent = [...gd.rounds].reverse().find(r => r.present);
      const rateVal = counterRates[key] !== undefined ? parseFloat(counterRates[key]) || 0 : (lastPresent?.rate || 0);
      const qtyVal = counterRates[`${key}_qty`] !== undefined ? parseInt(counterRates[`${key}_qty`]) || gd.qty : gd.qty;
      return { grade: gd.grade, sku: gd.sku, qty: qtyVal, rate: rateVal, customer_rate: rateVal };
    }).filter(i => !counterRates[`${i.grade}|${i.sku || 'TIN'}_delete`]);

    // Add newly added grades
    newGrades.forEach(ng => {
      if (ng.grade && ng.qty > 0 && ng.rate > 0) {
        items.push({ grade: ng.grade, sku: ng.sku || 'TIN', qty: parseInt(ng.qty), rate: parseFloat(ng.rate), customer_rate: parseFloat(ng.rate) });
      }
    });

    // Build cancel remarks into notes
    const cancelRemarks = allGradeKeys
      .filter(key => counterRates[`${key}_delete`] && counterRates[`${key}_cancel_remark`])
      .map(key => `${gradeMap[key].grade}: ${counterRates[`${key}_cancel_remark`]}`)
      .join('; ');
    const fullNotes = [counterNotes, cancelRemarks].filter(Boolean).join(' | ');

    try {
      await axios.put(endpoint, { items, notes: fullNotes });
      toast.success("Counter sent!");
      setShowCounterRow(false);
      setCounterRates({});
      setCounterNotes("");
      setNewGrades([]);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  const confirmDeal = async () => {
    try {
      await axios.put(`${API}/order-requests/${request.id}/confirm`, { confirmed_by: viewerRole });
      toast.success("Deal confirmed!");
      onUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rejectDeal = async () => {
    setRejectDialogOpen(true);
  };

  const submitReject = async () => {
    try {
      await axios.put(`${API}/order-requests/${request.id}/reject`, { rejected_by: viewerRole, reason: rejectRemark });
      toast.success("Deal rejected");
      setRejectDialogOpen(false);
      setRejectRemark("");
      onUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const statusMap = {
    pending: { cls: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
    negotiating: { cls: 'bg-amber-100 text-amber-800', label: 'Negotiating' },
    countered: { cls: 'bg-amber-100 text-amber-800', label: 'Countered' },
    processing: { cls: 'bg-blue-100 text-blue-800', label: 'Order in Process' },
    confirmed: { cls: 'bg-green-100 text-green-800', label: 'Confirmed' },
    ordered: { cls: 'bg-emerald-100 text-emerald-800', label: 'Ordered' },
    order_cancelled: { cls: 'bg-red-100 text-red-800', label: 'Order Cancelled' },
    rejected: { cls: 'bg-red-100 text-red-800', label: 'Rejected' },
  };
  const statusBadgeCls = (statusMap[request.status] || statusMap.pending).cls;
  const statusLabel = (statusMap[request.status] || { label: request.status }).label;

  const visibleHeaders = [...historyHeaders, ...latestHeaders];

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid={`negotiation-${request.id}`}>
      {/* Header with IST timestamp */}
      <div className="p-2 bg-secondary/20 border-b flex items-center justify-between flex-wrap gap-1">
        <div className="flex items-center gap-2">
          {request.enquiry_number && <Badge variant="outline" className="text-[10px] font-mono">{request.enquiry_number}</Badge>}
          <Badge className={`${statusBadgeCls} text-[9px]`}>{statusLabel}</Badge>
          {request.order_number && <Badge className="bg-green-100 text-green-800 text-[9px]">{request.order_number}</Badge>}
          <span className="text-[10px] text-muted-foreground">({roundHeaders.length} rounds)</span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Auto-fill rate buttons — owner only */}
          {viewerRole === 'owner' && isActive && !isPreview && marketTrends && (
            <div className="flex items-center gap-0.5 mr-1">
              <button onClick={() => autoFillRates('cr')} className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors" title="Fill Customer Rate">CR</button>
              <button onClick={() => autoFillRates('bsr1')} className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors" title="Fill BSR-1 (CR+100)">BSR-1</button>
              <button onClick={() => autoFillRates('bsr2')} className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors" title="Fill BSR-2 (CR+200)">BSR-2</button>
              <button onClick={() => autoFillRates('bsr3')} className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors" title="Fill BSR-3 (CR+300)">BSR-3</button>
            </div>
          )}
          <span className="text-[10px] font-bold text-muted-foreground">{formatIST(request.created_at)} {formatISTTime(request.created_at)}</span>
          {viewerRole === 'owner' && isActive && !isPreview && (
            <button onClick={rejectDeal} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded p-0.5 transition-colors" title="Cancel this thread" data-testid={`cancel-thread-${request.id}`}>
              <XCircle className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* History toggle */}
      {hasHistory && (
        <button onClick={() => setShowHistory(!showHistory)}
          className="w-full text-[10px] text-muted-foreground hover:text-foreground py-1 px-2 bg-gray-50/50 border-b flex items-center justify-center gap-1 transition-colors">
          {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showHistory ? 'Hide' : 'Show'} previous rounds ({roundHeaders.length - 2})
        </button>
      )}

      {/* Column-based table — show Qty/Rate per round */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left py-1.5 px-2 font-semibold">Grade</th>
              <th className="text-left py-1.5 px-2 font-semibold">SKU</th>
              {autoRates && <th className="text-center py-1.5 px-1.5 font-semibold text-[9px] text-blue-700">Auto Rate</th>}
              {products && viewerRole !== 'customer' && <th className="text-center py-1.5 px-1.5 font-semibold text-[9px]">Eff. Stock</th>}
              {visibleHeaders.map((rh, i) => {
                // Compute weighted avg rate per pack for this round (admin only)
                const neg = negotiations.find(n => n.round === rh.round && !n.action);
                let avgPerKg = '';
                if (viewerRole !== 'customer' && neg) {
                  const totalVal = (neg.items || []).reduce((s, it) => s + (it.qty || 0) * (it.rate || 0), 0);
                  const totalKgs = (neg.items || []).reduce((s, it) => {
                    const sku = (it.sku || '').toUpperCase();
                    const grade = (it.grade || '').toUpperCase();
                    const NINE_KG = ['JH','SJH','JJH','W150','W180','180','A180','PG'];
                    const kg = NINE_KG.includes(grade) ? 9 : 10;
                    return s + (it.qty || 0) * kg;
                  }, 0);
                  avgPerKg = totalKgs > 0 ? `₹${Math.round(totalVal / totalKgs).toLocaleString('en-IN')}/kg` : '';
                }
                return (
                <th key={i} className={`text-center py-1 px-2 font-semibold whitespace-nowrap ${rh.from === 'customer' ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
                  <div>{rh.label}</div>
                  <div className="text-[8px] font-normal opacity-70">{formatIST(rh.timestamp)} {formatISTTime(rh.timestamp)}</div>
                  {avgPerKg && <div className="text-[10px] font-extrabold text-emerald-700">{avgPerKg}</div>}
                </th>
                );
              })}
              {isActive && myTurn && showCounterRow && !isPreview && (() => {
                // Live avg/kg from counter rates
                const NINE_KG = ['JH','SJH','JJH','W150','W180','180','A180','PG'];
                let liveVal = 0, liveKgs = 0;
                allGradeKeys.forEach(key => {
                  const gd = gradeMap[key];
                  if (counterRates[`${key}_delete`]) return;
                  const isRemoved = (request.removed_grades || []).includes(`${gd.grade}|${gd.sku}`);
                  if (isRemoved) return;
                  const qtyVal = parseInt(counterRates[`${key}_qty`] ?? (() => { const lp = [...gd.rounds].reverse().find(r => r.present); return lp?.qty || 0; })());
                  const rateVal = parseFloat(counterRates[key] ?? (() => { const lp = [...gd.rounds].reverse().find(r => r.present); return lp?.rate || 0; })());
                  const kg = NINE_KG.includes(gd.grade.toUpperCase()) ? 9 : 10;
                  liveVal += qtyVal * rateVal;
                  liveKgs += qtyVal * kg;
                });
                newGrades.forEach(ng => {
                  const q = parseInt(ng.qty) || 0;
                  const r = parseFloat(ng.rate) || 0;
                  const kg = NINE_KG.includes((ng.grade || '').toUpperCase()) ? 9 : 10;
                  liveVal += q * r;
                  liveKgs += q * kg;
                });
                const liveAvg = liveKgs > 0 ? `₹${Math.round(liveVal / liveKgs).toLocaleString('en-IN')}/kg` : '';
                return (
                  <th className="text-center py-1.5 px-2 font-semibold bg-emerald-50 text-emerald-800 whitespace-nowrap">
                    <div>Your Counter</div>
                    {liveAvg && <div className="text-[10px] font-extrabold text-emerald-700">{liveAvg}</div>}
                  </th>
                );
              })()}
            </tr>
          </thead>
          <tbody>
            {allGradeKeys.map(key => {
              const gd = gradeMap[key];
              const isDeleted = counterRates[`${key}_delete`];
              const visibleRoundIdxStart = hasHistory && !showHistory ? Math.max(0, gd.rounds.length - 2) : 0;
              const visibleRounds = gd.rounds.slice(visibleRoundIdxStart);
              const isRemovedInLatest = gd.rounds.length > 0 && !gd.rounds[gd.rounds.length - 1]?.present;
              // Permanently removed by owner — check removed_grades list
              const isPermanentlyRemoved = (request.removed_grades || []).includes(`${gd.grade}|${gd.sku}`);
              const isRemoved = isRemovedInLatest || isPermanentlyRemoved;
              return (
                <tr key={key} className={`border-b last:border-0 group ${isDeleted ? 'opacity-30 line-through' : ''} ${isRemoved && !isActive ? 'opacity-50' : ''}`}>
                  <td className={`py-1.5 px-2 font-semibold ${isRemoved ? 'line-through text-red-400' : ''}`}>
                    <div className="flex items-center gap-0.5">
                      <span>{gd.grade}</span>
                      {viewerRole === 'owner' && isActive && !isPreview && !isRemoved && (
                        <button onClick={() => { setGradeCancelDialog({ key, grade: gd.grade, sku: gd.sku }); setGradeCancelRemark(""); }}
                          className="text-red-300 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 ml-auto shrink-0" title={`Cancel ${gd.grade}`}>
                          <XCircle className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className={`py-1.5 px-2 text-muted-foreground ${isRemovedInLatest ? 'line-through text-red-400' : ''}`}>{gd.sku}</td>
                  {autoRates && (() => {
                    const ar = getAutoRate(gd.grade, gd.sku);
                    return <td className="py-1.5 px-1.5 text-center text-[10px] font-semibold text-blue-600">{ar ? `₹${ar.toLocaleString('en-IN')}` : '-'}</td>;
                  })()}
                  {products && viewerRole !== 'customer' && (() => {
                    const stock = getStock(gd.grade, gd.sku);
                    return <td className={`py-1.5 px-1.5 text-center text-[10px] font-semibold ${stock > 100 ? 'text-green-600' : stock > 0 ? 'text-amber-600' : 'text-red-500'}`}>{stock !== null ? stock : '-'}</td>;
                  })()}
                  {visibleRounds.map((r, ri) => {
                    const absoluteIdx = visibleRoundIdxStart + ri;
                    const prevRound = absoluteIdx > 0 ? gd.rounds[absoluteIdx - 1] : null;
                    const isRemoved = !r.present;
                    const wasPresent = prevRound?.present;
                    const isNewlyAdded = r.present && prevRound && !prevRound.present;
                    const isRateEdited = r.present && prevRound?.present && r.rate !== prevRound.rate && prevRound.rate !== null;
                    const isQtyEdited = r.present && prevRound?.present && r.qty !== prevRound.qty && prevRound.qty !== null;
                    // Extract grade-specific remark from round notes
                    const roundNeg = negotiations.filter(n => !n.action)[absoluteIdx];
                    const roundNotes = roundNeg?.notes || '';
                    const gradeRemark = isRemoved && wasPresent ? (() => {
                      const match = roundNotes.match(new RegExp(`${gd.grade}:\\s*([^|;]+)`));
                      return match ? match[1].trim() : '';
                    })() : '';
                    return (
                      <td key={ri} className={`py-1.5 px-2 text-center ${isRemoved ? 'bg-red-50/70' : r.from === 'customer' ? 'bg-amber-50/50' : 'bg-blue-50/50'}`}>
                        {isRemoved ? (
                          <div>
                            <span className="text-red-400 line-through text-[10px] italic">removed</span>
                            {gradeRemark && <span className="block text-[7px] text-red-500 italic mt-0.5" title={gradeRemark}>{gradeRemark.length > 20 ? gradeRemark.slice(0, 20) + '…' : gradeRemark}</span>}
                          </div>
                        ) : (
                          <>
                            <span className={`font-bold ${isRateEdited ? 'text-orange-700 bg-orange-100 px-0.5 rounded' : ''} ${isNewlyAdded ? 'text-green-700' : ''}`}>
                              {r.qty}/{r.rate !== null ? `₹${r.rate.toLocaleString('en-IN')}` : '-'}
                            </span>
                            {isQtyEdited && (
                              <span className="block text-[8px] text-blue-600 font-semibold">
                                qty {prevRound.qty}→{r.qty}
                              </span>
                            )}
                            {isRateEdited && (
                              <span className="block text-[8px] text-orange-600 font-semibold">
                                ₹{prevRound.rate?.toLocaleString('en-IN')}→₹{r.rate?.toLocaleString('en-IN')}
                              </span>
                            )}
                            {isNewlyAdded && (
                              <span className="block text-[8px] text-green-600 font-semibold">+added</span>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                  {isActive && myTurn && showCounterRow && !isPreview && (
                    <td className="py-1 px-1.5 bg-emerald-50/50">
                      {(isRemoved || isRemovedInLatest) ? (
                        <span className="text-[9px] text-red-400 italic px-1">removed</span>
                      ) : !isDeleted ? (
                        <div className="flex items-center gap-1">
                          <Input type="number"
                            className="h-6 w-12 text-[10px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            defaultValue={(() => { const last = [...gd.rounds].reverse().find(r => r.present); return last?.qty ?? gd.qty; })()}
                            placeholder="Qty"
                            onChange={e => setCounterRates(prev => ({ ...prev, [`${key}_qty`]: e.target.value }))}
                          />
                          <span className="text-[9px] text-muted-foreground">/</span>
                          <Input type="number"
                            className="h-6 w-16 text-[10px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            value={counterRates[key] !== undefined ? counterRates[key] : (() => { const last = [...gd.rounds].reverse().find(r => r.present); return last?.rate || ''; })()}
                            placeholder="Rate"
                            onChange={e => setCounterRates(prev => ({ ...prev, [key]: e.target.value }))}
                          />
                          <button onClick={() => setCounterRates(prev => ({ ...prev, [`${key}_delete`]: true }))}
                            className="text-red-400 hover:text-red-600 ml-0.5" title="Remove grade">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setCounterRates(prev => { const p = {...prev}; delete p[`${key}_delete`]; return p; })}
                          className="text-[9px] text-blue-600 underline">Undo</button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {/* New grade rows for counter — with autocomplete dropdowns */}
            {isActive && myTurn && showCounterRow && !isPreview && newGrades.map((ng, ngIdx) => {
              const removedGradeKeys = new Set(request.removed_grades || []);
              const gradeList = products ? [...new Set(products.map(p => p.grade).filter(Boolean))].sort() : [];
              // Filter out permanently removed grades from the dropdown for ALL roles
              const availableGrades = gradeList.filter(g => {
                return !['TIN','BUCKET'].some(s => removedGradeKeys.has(`${g}|${s}`));
              });
              const skuList = products ? [...new Set(products.map(p => p.sku_type || p.sku).filter(Boolean))].sort() : ['TIN', 'BUCKET'];
              const filteredGrades = ng.gradeSearch ? availableGrades.filter(g => g.toLowerCase().includes(ng.gradeSearch.toLowerCase())).slice(0, 10) : [];
              return (
              <tr key={`new-${ngIdx}`} className="border-b bg-green-50/40">
                <td className="py-1 px-2 relative">
                  <Input value={ng.gradeSearch || ng.grade}
                    onChange={e => setNewGrades(prev => prev.map((g, i) => i === ngIdx ? {...g, gradeSearch: e.target.value, grade: ''} : g))}
                    placeholder="Grade..." className="h-6 w-20 text-[10px] px-1" />
                  {ng.gradeSearch && !ng.grade && filteredGrades.length > 0 && (
                    <div className="absolute z-50 mt-0.5 left-1 max-h-28 overflow-y-auto border rounded bg-white shadow-lg text-[10px] w-24">
                      {filteredGrades.map(g => (
                        <div key={g} className="px-1.5 py-1 cursor-pointer hover:bg-green-50 border-b last:border-0"
                          onClick={() => setNewGrades(prev => prev.map((gr, i) => i === ngIdx ? {...gr, grade: g, gradeSearch: g} : gr))}>{g}</div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-1 px-2">
                  <select value={ng.sku} onChange={e => setNewGrades(prev => prev.map((g, i) => i === ngIdx ? {...g, sku: e.target.value} : g))}
                    className="h-6 w-16 text-[10px] border rounded px-0.5 bg-white">
                    {skuList.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                {products && viewerRole !== 'customer' && <td className="text-center text-[9px]">
                  {ng.grade ? (() => { const s = getStock(ng.grade, ng.sku); return s !== null ? s : '-'; })() : ''}
                </td>}
                {visibleHeaders.map((_, i) => <td key={i}></td>)}
                <td className="py-1 px-1.5 bg-emerald-50/50">
                  <div className="flex items-center gap-1">
                    <Input type="number" value={ng.qty} onChange={e => setNewGrades(prev => prev.map((g, i) => i === ngIdx ? {...g, qty: e.target.value} : g))}
                      placeholder="Qty" className="h-6 w-12 text-[10px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    <span className="text-[9px] text-muted-foreground">/</span>
                    <Input type="number" value={ng.rate} onChange={e => setNewGrades(prev => prev.map((g, i) => i === ngIdx ? {...g, rate: e.target.value} : g))}
                      placeholder="Rate" className="h-6 w-16 text-[10px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                    <button onClick={() => setNewGrades(prev => prev.filter((_, i) => i !== ngIdx))} className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
            {isActive && !isPreview && (
              <tr className="bg-emerald-50/30">
                <td colSpan={99} className="py-1 px-2">
                  <button onClick={() => { setNewGrades(prev => [...prev, { grade: '', sku: 'TIN', qty: '', rate: '', gradeSearch: '' }]); if (!showCounterRow) setShowCounterRow(true); }}
                    className="text-[10px] text-emerald-700 font-semibold hover:text-emerald-900">+ Add Grade</button>
                </td>
              </tr>
            )}
            {/* Total Qty row */}
            <tr className="border-t-2 border-gray-300 bg-gray-50/80">
              <td className="py-1.5 px-2 text-[10px] font-extrabold text-gray-700" colSpan={(autoRates ? 3 : 2) + (products && viewerRole !== 'customer' ? 1 : 0)}>Total Qty</td>
              {visibleHeaders.map((rh, ri) => {
                const neg = negotiations.find(n => n.round === rh.round && !n.action);
                if (!neg) return <td key={ri}></td>;
                const roundQty = (neg.items || []).reduce((s, i) => s + (i.qty || 0), 0);
                return (
                  <td key={ri} className={`py-1.5 px-1 text-center text-[10px] font-extrabold ${neg.from === 'customer' ? 'text-amber-800' : 'text-blue-800'}`}>
                    {roundQty > 0 ? roundQty : '-'}
                  </td>
                );
              })}
              {isActive && myTurn && showCounterRow && !isPreview && (
                <td className="py-1.5 px-1.5 text-center text-[10px] font-extrabold text-emerald-700 bg-emerald-50/50">
                  {(() => {
                    let total = 0;
                    allGradeKeys.forEach(key => {
                      const gd = gradeMap[key];
                      if (counterRates[`${key}_delete`]) return;
                      const qtyVal = counterRates[`${key}_qty`];
                      if (qtyVal !== undefined) { total += parseInt(qtyVal) || 0; return; }
                      const lastPresent = [...gd.rounds].reverse().find(r => r.present);
                      total += lastPresent?.qty || 0;
                    });
                    newGrades.forEach(ng => { total += parseInt(ng.qty) || 0; });
                    return total > 0 ? total : '-';
                  })()}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Notes/Remarks from rounds with IST timestamps */}
      {(() => {
        const notesRounds = negotiations.filter(n => n.notes && !n.action).slice(-3);
        return notesRounds.length > 0 && (
          <div className="px-2 py-1 border-t bg-gray-50/50 space-y-0.5">
            {notesRounds.map((n, i) => (
              <p key={i} className="text-[10px] text-muted-foreground">
                <span className={`font-semibold ${n.from === 'customer' ? 'text-amber-700' : 'text-blue-700'}`}>
                  R{n.round} ({formatISTTime(n.timestamp)}):
                </span> {n.notes}
              </p>
            ))}
          </div>
        );
      })()}

      {/* Counter row — with remark/notes field */}
      {isActive && myTurn && !isPreview && !showCounterRow && (
        <div className="p-2 border-t bg-gray-50 flex items-center justify-between">
          <button onClick={() => setShowCounterRow(true)}
            className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 font-semibold px-2 py-1 rounded hover:bg-amber-50 transition-colors"
            data-testid={`start-round-${request.id}`}>
            <Star className="w-4 h-4 fill-amber-400 text-amber-500" /> New Round
          </button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-green-700 border-green-300"
              onClick={confirmDeal} disabled={sending} data-testid={`confirm-deal-${request.id}`}>
              <CheckCircle className="w-3 h-3" /> Confirm
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={rejectDeal}>
              <XCircle className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
      {isActive && myTurn && !isPreview && showCounterRow && (
        <div className="p-2 border-t bg-emerald-50 space-y-2">
          <Textarea value={counterNotes} onChange={e => setCounterNotes(e.target.value)}
            placeholder="Add remark / reply..." rows={1} className="text-xs min-h-[32px] resize-none bg-white" />
          <div className="flex gap-2">
            <Button size="sm" className="h-7 text-xs gap-1 flex-1" onClick={sendCounter} disabled={sending}
              data-testid={`send-counter-${request.id}`}>
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send Counter
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowCounterRow(false); setCounterNotes(""); }}>Cancel</Button>
          </div>
        </div>
      )}
      {isActive && !myTurn && (
        <div className="p-1.5 border-t bg-gray-50 text-center">
          <p className="text-[10px] text-muted-foreground">
            Waiting for {waitingForOwner ? (viewerRole === 'owner' ? 'your' : 'owner') : (viewerRole === 'customer' ? 'your' : 'customer')} response...
          </p>
        </div>
      )}
      {request.status === 'processing' && (
        <div className="p-1.5 border-t bg-blue-50 flex items-center justify-center gap-1 text-blue-700 text-xs font-semibold">
          <CheckCircle className="w-3.5 h-3.5" /> Deal confirmed{actionBy ? ` by ${actionBy}` : ''} — Awaiting order creation
        </div>
      )}
      {request.status === 'confirmed' && (
        <div className="p-1.5 border-t bg-green-50 flex items-center justify-center gap-1 text-green-700 text-xs font-semibold">
          <CheckCircle className="w-3.5 h-3.5" /> Deal confirmed{actionBy ? ` by ${actionBy}` : ''} {request.order_number && `— Order ${request.order_number}`}
        </div>
      )}
      {request.status === 'ordered' && (
        <div className="p-1.5 border-t bg-emerald-50 flex items-center justify-center gap-1 text-emerald-700 text-xs font-semibold">
          <CheckCircle className="w-3.5 h-3.5" /> Order created{actionBy ? ` by ${actionBy}` : ''} {request.order_number && `— ${request.order_number}`}
        </div>
      )}
      {request.status === 'order_cancelled' && (
        <div className="p-1.5 border-t bg-red-50 flex items-center justify-center gap-1 text-red-600 text-xs font-semibold">
          <XCircle className="w-3.5 h-3.5" /> Order cancelled{actionBy ? ` by ${actionBy}` : ''} {request.order_number && `— ${request.order_number}`}
        </div>
      )}
      {request.status === 'rejected' && (
        <div className="p-1.5 border-t bg-red-50 flex items-center justify-center gap-1 text-red-600 text-xs">
          <XCircle className="w-3.5 h-3.5" /> Rejected{actionBy ? ` by ${actionBy}` : ''}
          {request.reject_reason && <span className="text-[9px] text-red-500 ml-1">— {request.reject_reason}</span>}
        </div>
      )}

      {/* Grade cancel remark dialog — Owner gets dropdown, Customer gets simple text */}
      <Dialog open={!!gradeCancelDialog} onOpenChange={(open) => { if (!open) { setGradeCancelDialog(null); setGradeCancelRemark(""); } }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-sm">Cancel Grade — {gradeCancelDialog?.grade} {gradeCancelDialog?.sku}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">This grade will be removed from your next counter.</p>
            {viewerRole === 'owner' ? (
              <>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground">Quick Remark:</p>
                  <div className="flex flex-wrap gap-1">
                    {["Grade sold out", "Price not viable", "Stock unavailable", "Customer unresponsive", "Quality issue", "Replaced with alternate"].map(opt => (
                      <button key={opt} onClick={() => setGradeCancelRemark(opt)}
                        className={`text-[9px] px-2 py-1 rounded-full border transition-colors ${gradeCancelRemark === opt ? 'bg-red-100 border-red-400 text-red-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea value={gradeCancelRemark} onChange={e => setGradeCancelRemark(e.target.value)}
                  placeholder="Or type custom remark..." className="text-xs min-h-[40px]" />
              </>
            ) : (
              <Textarea value={gradeCancelRemark} onChange={e => setGradeCancelRemark(e.target.value)}
                placeholder="Reason (optional)..." className="text-xs min-h-[50px]" />
            )}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setGradeCancelDialog(null); setGradeCancelRemark(""); }}>Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => {
                if (!gradeCancelDialog) return;
                const k = gradeCancelDialog.key;
                setCounterRates(prev => ({ ...prev, [`${k}_delete`]: true, [`${k}_cancel_remark`]: gradeCancelRemark }));
                if (!showCounterRow) setShowCounterRow(true);
                toast.success(`${gradeCancelDialog.grade} marked for removal`);
                setGradeCancelDialog(null);
                setGradeCancelRemark("");
              }}>Remove Grade</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject/Cancel remark dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Cancel / Reject Thread</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Add a remark (optional) — this will be visible to the customer.</p>
            <Textarea value={rejectRemark} onChange={e => setRejectRemark(e.target.value)}
              placeholder="Reason for cancellation..." className="text-xs min-h-[60px]" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectRemark(""); }}>Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={submitReject}>Confirm Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
